import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import {
    BodyTextSmall,
    HeadingText20,
    HeadingText30,
    Icon,
    pxToRem,
} from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { filterNullish } from '@modernhealth/filter-nullish';
import { useAnalytics } from '@modernhealth/web-analytics';
import { EventLexicon } from '@modernhealth/analytics-lexicon';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type {
    RecommendedActivitiesFragment,
    PathwayAssessmentActivityFragment,
    PathwayProviderFragment,
} from '../documents/fragments.generated';
import { mapToContentLink } from '../helpers/mapToContentLink';
import type { ActivityLink } from '../helpers/mapToContentLink';
import { usePathwaysNavigation } from '../hooks/navigation';
import pencilIcon from '../assets/pencil.svg';
import assessmentIcon from '../assets/assessment.svg';
import { PathwayProgressState } from 'src/generated/webapp_gql';

export interface RecommendedActivitiesProps {
    pathwayUuid: string;
    pathwayProvider?: PathwayProviderFragment;
    pathwayProgress?: PathwayProgressState;
    topicHeader?: string;
    title: string;
    description: string;
    activities?: (RecommendedActivitiesFragment | null)[] | null;
    assessmentActivity?: PathwayAssessmentActivityFragment | null;
}

export const RecommendedActivities: React.FC<RecommendedActivitiesProps> = ({
    pathwayUuid,
    pathwayProvider,
    pathwayProgress,
    topicHeader,
    title,
    description,
    activities: recommendedActivities,
    assessmentActivity,
}) => {
    const activities = useMemo(
        () => filterNullish(recommendedActivities ?? []),
        [recommendedActivities],
    );

    return (
        <Wrapper>
            <HeadingText30>{title}</HeadingText30>
            <BodyTextSmall marginBottom={10}>{description}</BodyTextSmall>
            {assessmentActivity && (
                <AssessmentActivity
                    pathwayUuid={pathwayUuid}
                    pathwayProvider={pathwayProvider}
                    pathwayProgress={pathwayProgress}
                    topicHeader={topicHeader}
                    assessmentActivity={assessmentActivity}
                />
            )}
            {activities.map(activity => {
                const link = mapToContentLink(activity);
                if (!link) return;
                return (
                    <Activity
                        key={link.uuid}
                        link={link}
                        pathwayUuid={pathwayUuid}
                        pathwayProvider={pathwayProvider}
                        pathwayProgress={pathwayProgress}
                        topicHeader={topicHeader}
                    />
                );
            })}
        </Wrapper>
    );
};

const AssessmentMediaType = 'Assessment';

const AssessmentActivity = ({
    pathwayUuid,
    pathwayProvider,
    pathwayProgress,
    topicHeader,
    assessmentActivity,
}: {
    pathwayUuid: string;
    pathwayProvider?: PathwayProviderFragment;
    pathwayProgress?: PathwayProgressState;
    topicHeader?: string;
    assessmentActivity: PathwayAssessmentActivityFragment;
}) => {
    const { t } = useTranslation();
    const navigation = usePathwaysNavigation();

    return (
        <Activity
            link={{
                name:
                    assessmentActivity.assessment?.displayName ??
                    t('pathways:components.recommended_activities.activity_type.assessment'),
                image: assessmentIcon,
                isCompleted: !!assessmentActivity.isCompleted,
                link: navigation.locationDescriptor.pathwayAssessment({
                    uuid: pathwayUuid,
                }),
                mediaType: {
                    name: 'Assessment',
                    icon: pencilIcon,
                },
            }}
            pathwayUuid={pathwayUuid}
            pathwayProvider={pathwayProvider}
            pathwayProgress={pathwayProgress}
            topicHeader={topicHeader}
        />
    );
};

const Activity: React.FC<{
    link: ActivityLink;
    pathwayUuid: string;
    pathwayProvider?: PathwayProviderFragment;
    pathwayProgress?: PathwayProgressState;
    topicHeader?: string;
}> = ({ link, pathwayUuid, pathwayProvider, pathwayProgress, topicHeader }) => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const getActivityTypeLabel = (mediaTypeName: string) => {
        if (mediaTypeName === AssessmentMediaType) {
            return t('pathways:components.recommended_activities.activity_type.assessment');
        }
        return mediaTypeName;
    };

    return (
        <ActivityContainer
            to={link.link}
            onClick={() => {
                if (pathwayProvider && pathwayProgress && topicHeader) {
                    analytics.trackEvent(
                        EventLexicon.clickPathwayRecommendedActivity({
                            providerUuid: pathwayProvider.provider.uuid,
                            pathwayUuid: pathwayUuid,
                            pathwayProgress: pathwayProgress,
                            translationKey: link.name,
                            pathwayActivityUuid: link.uuid as string,
                            pathwayTopicTitle: topicHeader,
                        }),
                    );
                }
            }}
        >
            <TitleWrapper>
                <ImageContainer>
                    {link.isCompleted && (
                        <IconContainer
                            name="checkmark-enclosed-filled"
                            color="green55"
                            size={20}
                            aria-label={t(
                                'pathways:components.provider_details_modal.completed_aria_label',
                                { activity: link.name },
                            )}
                        />
                    )}
                    {link.image && (
                        <Circle isCompleted={link.isCompleted}>
                            <Image src={link.image} alt={link.name} />
                        </Circle>
                    )}
                </ImageContainer>
                <TitleInnerWrapper isCompleted={link.isCompleted}>
                    <HeadingText20>{link.name}</HeadingText20>
                    {link.mediaType && (
                        <SubtitleWrapper>
                            <img src={link.mediaType.icon} alt={link.name} height={12} />
                            <BodyTextSmall marginLeft={5}>
                                {link.duration
                                    ? t(
                                          'pathways:components.recommended_activities.activity_length',
                                          {
                                              activityType: getActivityTypeLabel(
                                                  link.mediaType.name,
                                              ),
                                              duration: link.duration,
                                          },
                                      )
                                    : getActivityTypeLabel(link.mediaType.name)}
                            </BodyTextSmall>
                        </SubtitleWrapper>
                    )}
                </TitleInnerWrapper>
            </TitleWrapper>
            <ChevronContainer>
                <Icon name="chevron-right" />
            </ChevronContainer>
        </ActivityContainer>
    );
};

const IMAGE_CONTAINER_HEIGHT = 50;

const TitleWrapper = styled.div({
    display: 'flex',
});

const TitleInnerWrapper = styled.div<{ isCompleted: boolean }>(({ isCompleted }) => ({
    marginLeft: pxToRem(5),
    opacity: isCompleted ? 0.5 : 1,
}));

const Circle = styled.div<{ isCompleted: boolean }>(({ isCompleted }) => ({
    height: IMAGE_CONTAINER_HEIGHT,
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: isCompleted ? 0.5 : 1,
}));

const IconContainer = styled(Icon)({
    position: 'absolute',
    right: -5,
    top: 0,
    zIndex: 1,
});

const ChevronContainer = styled.div({
    cursor: 'pointer',
    padding: pxToRem(5),
});

const SubtitleWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
});

const Image = styled.img({
    height: IMAGE_CONTAINER_HEIGHT * 1.25,
    objectFit: 'cover',
    objectPosition: 'center',
});

const ImageContainer = styled.div({
    width: IMAGE_CONTAINER_HEIGHT,
    height: IMAGE_CONTAINER_HEIGHT,
    margin: '0 auto',
    display: 'block',
    marginRight: pxToRem(10),
    position: 'relative',
});

const Wrapper = styled.div({
    width: '100%',
    padding: pxToRem(24),
    marginTop: pxToRem(24),
    borderRadius: 8,
    backgroundColor: colors.gray05,
});

const ActivityContainer = styled(Link)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: pxToRem(16, 0),
    cursor: 'pointer',
    borderRadius: 8,
    color: colors.black,
    transition: 'ease-in-out all 200ms',
    ':hover': {
        backgroundColor: colors.white,
        color: colors.black,
    },
});
